import FormHelperText from '@mui/material/FormHelperText';
import {
  BaseTextField,
  Card,
  ControlledCheckboxSelect,
  ControlledTextField,
  StyledGrid,
  StyledGridItem,
} from 'modules/shared/components';
import {
  INPUT_MODES,
  INPUT_TYPES,
} from 'modules/shared/components/form/TextField/textField.constants';
import { getErrorByPath, splitMinutes, splitMinutesFromTimeString } from 'modules/shared/utils';
import { TaskName } from 'modules/work-implementation/project-view/shared/components';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { TASK_ASSIGNMENT_ERRORS, TASK_ASSIGNMENT_FIELDS, TASK_PRIORITY } from '../../constants';
import { EMPLOYEE_SCHEDULE_FIELDS } from '../../constants/employeeSchedule.constants';
import { mapAssignmentToApi, mapEditAssignmentToApi } from '../utils/assignmentPayload.mapper';
import EmployeeAssignmentField from './EmployeeAssignmentField';
import PrioritySelector from './PrioritySelector';
import TaskAssignmentField from './TaskAssignmentField';

// TODO: move to taskAssignment.constants.js on completion of task assignment form
export const TASK_ASSIGNMENT_DEFAULT_VALUES = Object.freeze({
  [EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID]: null,
  [EMPLOYEE_SCHEDULE_FIELDS.ISO_WEEK]: null,
  [TASK_ASSIGNMENT_FIELDS.ASSIGNED_TASKS]: [],
  [TASK_ASSIGNMENT_FIELDS.PRIORITY]: TASK_PRIORITY.MEDIUM,
  [TASK_ASSIGNMENT_FIELDS.NOTE]: '',
  [TASK_ASSIGNMENT_FIELDS.INWARD_DOCUMENTS]: [],
});

function getTotalMinutes(assignedTasks = []) {
  return assignedTasks.reduce((sum, task) => sum + (task.minutes ?? 0), 0);
}

/**
 * TaskAssignmentForm
 *
 * Controlled assignment form for a single task.
 * Exactly one instance may be active at a time.
 *
 * Behavior:
 * - Form state resets on activation
 * - Form state resets on cancellation
 * - Submission is async-safe
 * - Parent owns active state and persistence
 *
 * @param {Object} props
 * @param {Object} props.task - Task metadata (includes taskKey, type)
 * @param {Object} props.projectData - Project context (code, name, inwardDocuments)
 * @param {Array<Object>} props.taskList - Assignable task items
 * @param {boolean} props.isActive - Whether this task is currently active
 * @param {Object} props.assignedTask - Assigned task data
 * @param {boolean} props.isLocked - Whether the task is locked
 * @param {(taskKey: string) => void} props.onActivate
 * @param {() => void} props.onCancel
 * @param {(taskId: number, payload: Object) => Promise<void>} props.onEdit
 * @param {(payload: Object) => Promise<void>} props.onAssign
 * @param {Array<Object>} props.employees
 * @param {boolean} props.isLoading
 * @returns {JSX.Element}
 */
export default function TaskAssignmentForm({
  task,
  projectData,
  taskList,
  isActive,
  assignedTask,
  onActivate,
  onCancel,
  onAssign,
  onEdit,
  employees,
  isLoading,
  isLocked,
}) {
  const isEditMode = Boolean(assignedTask);

  const defaultValues = useMemo(() => {
    if (!isEditMode) return { ...TASK_ASSIGNMENT_DEFAULT_VALUES };

    return {
      [EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID]: assignedTask.employee?.id ?? null,
      [TASK_ASSIGNMENT_FIELDS.PRIORITY]: assignedTask.priority,
      [TASK_ASSIGNMENT_FIELDS.NOTE]: assignedTask.note ?? '',
      [TASK_ASSIGNMENT_FIELDS.INWARD_DOCUMENTS]:
        assignedTask.documentsShared?.map((d) => d.id) ?? [],
    };
  }, [isEditMode, assignedTask]);

  const methods = useForm({
    defaultValues,
    mode: 'onSubmit',
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, errors },
    control,
    setError,
    clearErrors,
  } = methods;

  const employeeId = watch(EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID);
  const isoWeek = watch(EMPLOYEE_SCHEDULE_FIELDS.ISO_WEEK);
  const assignedTasks = watch(TASK_ASSIGNMENT_FIELDS.ASSIGNED_TASKS) || [];
  const hasAssignments = assignedTasks.length > 0;
  const shouldShowTimeline = isEditMode || hasAssignments;

  const timelineError = getErrorByPath(errors, TASK_ASSIGNMENT_ERRORS.TIMELINE);

  const totalMinutes = getTotalMinutes(assignedTasks);

  const timelineMinutes = isEditMode
    ? splitMinutesFromTimeString(assignedTask.allottedTime)
    : totalMinutes;

  const documentOptions = (projectData?.inwardDocuments || []).map((document) => ({
    value: document.id,
    label: document.name,
  }));

  const { hours, minutes } = splitMinutes(timelineMinutes);

  const validateAssignment = async (values) => {
    clearErrors();

    if (isEditMode) return true;

    const employeeId = values[EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID];
    const isoWeek = values[EMPLOYEE_SCHEDULE_FIELDS.ISO_WEEK];

    if (!employeeId || !isoWeek) {
      setError(TASK_ASSIGNMENT_ERRORS.EMPLOYEE, {
        type: 'manual',
        message: 'Employee and week must be selected',
      });
      return false;
    }

    const assignedTasks = values[TASK_ASSIGNMENT_FIELDS.ASSIGNED_TASKS];

    if (!assignedTasks.length) {
      setError(TASK_ASSIGNMENT_ERRORS.TASKS, {
        type: 'manual',
        message: 'At least one task must be assigned',
      });
      return false;
    }

    if (!isEditMode) {
      const totalMinutes = getTotalMinutes(assignedTasks);

      if (totalMinutes <= 0) {
        setError(TASK_ASSIGNMENT_ERRORS.TIMELINE, {
          type: 'manual',
          message: 'Work timeline must be greater than zero',
        });
        return false;
      }
    }

    const employeeAvailability = values[EMPLOYEE_SCHEDULE_FIELDS.AVAILABILITY_MINUTES];
    if (employeeAvailability < totalMinutes) {
      setError(TASK_ASSIGNMENT_ERRORS.EMPLOYEE, {
        type: 'manual',
        message: 'Assigned tasks exceed employee availability',
      });
      return false;
    }

    return true;
  };

  const handleAssign = async (values) => {
    const isValid = await validateAssignment(values);
    if (!isValid) return;

    if (isEditMode) {
      const editPayload = mapEditAssignmentToApi({
        values,
        assignedTask,
        task,
      });

      await onEdit(assignedTask.id, editPayload);
      return;
    }

    const assignmentPayload = mapAssignmentToApi({
      task,
      projectData,
      values,
      assignedTask,
    } as any);

    await onAssign(assignmentPayload);
  };

  useEffect(() => {
    if (isActive) {
      reset(defaultValues);
    }
  }, [isActive, reset, defaultValues]);

  return (
    <Card
      title={<TaskName taskName={task.name} />}
      action={
        !isActive && (
          <button
            type='button'
            className='btn cta-outlined-btn self-end'
            onClick={() => onActivate(task.taskKey)}
            disabled={isLocked}
          >
            Assign Work
          </button>
        )
      }
      colorVariant={isActive ? 'default' : 'disabled'}
      sx={{ px: 2, py: 2 }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleAssign)} className='column'>
          <StyledGrid columns={3}>
            <StyledGridItem columnIndex={0}>
              <span>Job code</span>
              <span className='value'>{projectData.code}</span>
            </StyledGridItem>
            <StyledGridItem columnIndex={1}>
              <span>Project name</span>
              <span className='value'>{projectData.name}</span>
            </StyledGridItem>
            <StyledGridItem columnIndex={2}>
              <EmployeeAssignmentField
                disabled={!isActive || isLoading || isEditMode}
                employees={employees}
              />
            </StyledGridItem>
          </StyledGrid>

          <StyledGrid columns={2}>
            <StyledGridItem columnIndex={0}>
              <span>Design/Drawings</span>
              <TaskAssignmentField
                disabled={!isActive || !employeeId || !isoWeek || isEditMode}
                isEditMode={isEditMode}
                taskName={task.name}
                tasks={taskList}
                taskType={task.type}
              />
            </StyledGridItem>
            <StyledGridItem columnIndex={1}>
              <span>Inward Documents</span>
              <ControlledCheckboxSelect
                name={TASK_ASSIGNMENT_FIELDS.INWARD_DOCUMENTS}
                options={documentOptions}
                showSelectAll
                disabled={!isActive || documentOptions.length === 0}
                placeholder='Select Inward Documents'
                selectAllLabel='Select All Inward Documents'
                wrapperSx={{ width: '60%', maxWidth: '360px' }}
              />
            </StyledGridItem>
          </StyledGrid>

          <StyledGrid columns={2}>
            <StyledGridItem columnIndex={0}>
              <span>Work timeline</span>

              <div className='row gap-1'>
                <BaseTextField
                  id='work-timeline-hours'
                  onChange={() => {}}
                  value={shouldShowTimeline ? hours : ''}
                  type={INPUT_TYPES.NUMBER}
                  inputMode={INPUT_MODES.NUMERIC}
                  readOnly
                  disabled
                  hideArrow
                  sx={{ width: 80 }}
                  suffix='hrs'
                  placeholder=''
                />
                <span>:</span>
                <BaseTextField
                  id='work-timeline-minutes'
                  onChange={() => {}}
                  value={shouldShowTimeline ? minutes : ''}
                  type={INPUT_TYPES.NUMBER}
                  inputMode={INPUT_MODES.NUMERIC}
                  readOnly
                  disabled
                  hideArrow
                  sx={{ width: 80 }}
                  suffix='mins'
                  placeholder=''
                />
              </div>
              {timelineError && <FormHelperText error>{timelineError.message}</FormHelperText>}
            </StyledGridItem>
            <StyledGridItem columnIndex={1}>
              <span>Priority</span>
              <Controller
                name={TASK_ASSIGNMENT_FIELDS.PRIORITY}
                control={control}
                rules={{ required: 'Priority is required' }}
                render={({ field }) => (
                  <PrioritySelector
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isActive}
                  />
                )}
              />
            </StyledGridItem>
          </StyledGrid>

          <ControlledTextField
            id='task-assignment-note'
            name={TASK_ASSIGNMENT_FIELDS.NOTE}
            rules={{
              required: 'Note/comment is required',
              maxLength: {
                value: 500,
                message: 'Cannot exceed 500 characters',
              },
            }}
            placeholder='Enter note/comment'
            isMultiline
            rows={4}
            showCharCount
            showErrorInline
            reserveErrorSpace
            label='Note/Comment'
            orientation='row'
            fullWidth
            wrapperSx={{ mt: 2 }}
          />

          <div className='row row--end'>
            <button
              type='button'
              className='btn outlined-btn'
              disabled={!isActive || isSubmitting}
              onClick={() => {
                reset(defaultValues);
                onCancel();
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='btn cta-btn'
              disabled={!isActive || isSubmitting || (!isEditMode && !hasAssignments)}
            >
              Assign Work
            </button>
          </div>
        </form>
      </FormProvider>
    </Card>
  );
}
